import { useCallback, useEffect, useRef, useState } from "react";
import { consultarStatusExportacao } from "../../../../services/apuracao.service";

/**
 * Rastreio de exportação assíncrona da Apuração (desenho 2026-10-02, seção 4).
 *
 * Fluxo: `enfileirar()` devolve o `correlationId` → polling em
 * GET /relatorios/apuracoes/:id/exportacoes/:correlationId → em `concluido`, apenas marca o
 * fim. SEM DOWNLOAD (2026-10-05): o worker Go já grava o arquivo solto em EXPORT_BASE_DIR
 * (pasta relatorios, 1.5/1.11) — não baixa mais pro navegador de quem clicou.
 *
 * Quem encerra o polling é o STATUS do servidor (`concluido`/`erro`/`expirado`, este último
 * aplicado lá pela regra de heartbeat), não um número fixo de tentativas: o consumer Kafka do
 * worker é sequencial e um job pode ficar minutos legitimamente na fila. No cliente fica só
 * um teto de segurança (20 min) — ao atingir, para de consultar e avisa (não é erro: o job
 * pode concluir depois, e um novo clique reencontra o mesmo job pela unicidade no servidor).
 *
 * Só `mensagem` (mensagem_usuario no servidor) chega ao usuário; o erro técnico nunca vem
 * na resposta de status.
 *
 * RETOMADA (2026-10-03): o `correlationId` é persistido em `localStorage`, por
 * `apuracaoId` + `chave` (string estável que o chamador escolhe — normalmente o `tipo` da
 * exportação). Ao montar, se existir um id salvo, o hook consulta o status direto (nunca
 * chama `enfileirar` de novo) e retoma o polling de onde o servidor diz que está — sem
 * recriar o job. Atualizar a página, trocar de aba e voltar, tudo preserva o progresso.
 * Sem `chave`, a retomada fica desligada (comportamento antigo) — todo chamador novo deve
 * passar uma.
 */

export const ESTADOS_EXPORTACAO = Object.freeze({
  OCIOSO: "ocioso",
  ENFILEIRANDO: "enfileirando",
  NA_FILA: "na_fila",
  PROCESSANDO: "processando",
  BAIXANDO: "baixando",
  CONCLUIDO: "concluido",
  ERRO: "erro",
  TEMPO_ESGOTADO: "tempo_esgotado",
});

const E = ESTADOS_EXPORTACAO;
const ESTADOS_ATIVOS = new Set([E.ENFILEIRANDO, E.NA_FILA, E.PROCESSANDO, E.BAIXANDO]);

export const OPCOES_PADRAO_EXPORTACAO = Object.freeze({
  intervaloInicialMs: 2000,
  intervaloLongoMs: 5000,
  trocaIntervaloAposMs: 30000,
  tetoMs: 20 * 60 * 1000,
  maxFalhasSeguidas: 3,
});

export const MENSAGENS_EXPORTACAO = Object.freeze({
  falhaEnfileirar: "Não foi possível solicitar a exportação.",
  falhaJob: "Não foi possível gerar o arquivo.",
  jobNaoEncontrado: "Exportação não encontrada. Exporte novamente.",
  falhaAcompanhar:
    "Não foi possível acompanhar a exportação. Verifique a conexão e tente novamente.",
  tetoAtingido: "A exportação continua em processamento; tente novamente mais tarde.",
  semRastreio: "O arquivo será entregue em breve.",
});

const PREFIXO_STORAGE = "txm:exportacaoAssincrona";

function chaveStorage(apuracaoId, chave) {
  return `${PREFIXO_STORAGE}:${apuracaoId}:${chave}`;
}

/** localStorage pode estar indisponível (modo privado, quota, contexto sem DOM) — nunca
 * deixar a feature de retomada derrubar o fluxo principal por causa disso. */
function lerCorrelationIdPersistido(apuracaoId, chave) {
  if (!apuracaoId || !chave) return null;
  try {
    return window.localStorage.getItem(chaveStorage(apuracaoId, chave)) || null;
  } catch {
    return null;
  }
}

function persistirCorrelationId(apuracaoId, chave, correlationId) {
  if (!apuracaoId || !chave) return;
  try {
    window.localStorage.setItem(chaveStorage(apuracaoId, chave), correlationId);
  } catch {
    /* sem persistência, só a retomada não funciona — não é erro fatal */
  }
}

function limparCorrelationIdPersistido(apuracaoId, chave) {
  if (!apuracaoId || !chave) return;
  try {
    window.localStorage.removeItem(chaveStorage(apuracaoId, chave));
  } catch {
    /* ignora */
  }
}

/** 2s nos primeiros 30s, 5s depois (desenho 4.2). */
export function calcularIntervaloPolling(decorridoMs, opcoes = OPCOES_PADRAO_EXPORTACAO) {
  return decorridoMs < opcoes.trocaIntervaloAposMs
    ? opcoes.intervaloInicialMs
    : opcoes.intervaloLongoMs;
}

function estadoDoStatusServidor(status) {
  return status === "processando" ? E.PROCESSANDO : E.NA_FILA;
}

// 400/403/404: o job não existe, não é do usuário ou o id é inválido — tentar de novo não
// muda nada. Qualquer outra falha (rede, 5xx) é tratada como transitória.
function falhaTerminal(httpStatus) {
  return httpStatus === 400 || httpStatus === 403 || httpStatus === 404;
}

const ESTADO_INICIAL = {
  estado: E.OCIOSO,
  percentual: null,
  descricao: null,
  mensagem: null,
  correlationId: null,
};

/**
 * @param {object} params
 * @param {string|number} params.apuracaoId
 * @param {string} [params.chave] Identifica esta "instância" de exportação (ex.: o `tipo`
 *   enviado a `enfileirar`) para persistência/retomada. Duas instâncias com a mesma
 *   `apuracaoId`+`chave` compartilham o job retomado — use o mesmo valor em telas diferentes
 *   só quando o `enfileirar` das duas aponta pro mesmo job no servidor.
 * @param {(...args: any[]) => Promise<{ sucesso: boolean, correlationId?: string, status?: string, mensagem?: string }>} params.enfileirar
 *   POST de enfileirar (sumarização ou eSocial); recebe os argumentos repassados a `iniciar`.
 * @param {(aviso: { severity: 'success'|'warning'|'error', msg: string }) => void} [params.onFinalizar]
 *   Chamado uma vez ao fim de cada execução, pra tela mostrar Snackbar no padrão dela.
 * @param {Partial<typeof OPCOES_PADRAO_EXPORTACAO>} [params.opcoes] Só pra teste/calibração.
 */
export default function useExportacaoAssincrona({
  apuracaoId,
  chave,
  enfileirar,
  onFinalizar,
  opcoes,
} = {}) {
  const [snap, setSnap] = useState(ESTADO_INICIAL);

  const apuracaoIdRef = useRef(apuracaoId);
  const chaveRef = useRef(chave);
  const enfileirarRef = useRef(enfileirar);
  const onFinalizarRef = useRef(onFinalizar);
  const opcoesRef = useRef({ ...OPCOES_PADRAO_EXPORTACAO, ...(opcoes || {}) });
  apuracaoIdRef.current = apuracaoId;
  chaveRef.current = chave;
  enfileirarRef.current = enfileirar;
  onFinalizarRef.current = onFinalizar;
  opcoesRef.current = { ...OPCOES_PADRAO_EXPORTACAO, ...(opcoes || {}) };

  const montadoRef = useRef(false);
  const execucaoRef = useRef(0);
  const ativoRef = useRef(false);
  const timerRef = useRef(null);

  /**
   * Acompanha um job já enfileirado (por `correlationId`) até concluir, falhar ou esgotar o
   * teto — e baixa os arquivos ao concluir. Reaproveitado por `iniciar` (job recém-criado) e
   * pelo efeito de retomada (job que já existia antes deste mount).
   *
   * `silenciosoSeNaoEncontrado`: true na retomada — se o id salvo não existir mais no
   * servidor (job expirou/foi de outro navegador), volta pra "ocioso" sem Snackbar de erro,
   * porque o usuário não clicou em nada desta vez.
   */
  const acompanharJob = useCallback(
    async (token, correlationId, { silenciosoSeNaoEncontrado = false } = {}) => {
      const vivo = () => montadoRef.current && execucaoRef.current === token;
      const op = opcoesRef.current;
      const idApuracao = apuracaoIdRef.current;
      const chaveAtual = chaveRef.current;

      const atualizar = (parcial) => {
        if (!vivo()) return;
        ativoRef.current = ESTADOS_ATIVOS.has(parcial.estado);
        setSnap((s) => ({ ...s, ...parcial }));
      };
      const notificar = (severity, msg) => {
        if (vivo()) onFinalizarRef.current?.({ severity, msg });
      };
      const falhar = (msg) => {
        limparCorrelationIdPersistido(idApuracao, chaveAtual);
        atualizar({ estado: E.ERRO, percentual: null, descricao: null, mensagem: msg });
        notificar("error", msg);
      };
      const esperar = (ms) =>
        new Promise((resolve) => {
          timerRef.current = setTimeout(resolve, ms);
        });

      const inicio = Date.now();
      let falhasSeguidas = 0;
      let primeiraConsulta = true;
      let status = null;

      for (;;) {
        const pularEspera = primeiraConsulta && silenciosoSeNaoEncontrado;
        primeiraConsulta = false;
        if (!pularEspera) {
          // Pula a espera só na 1ª consulta de uma retomada, pra sair do "ocioso" o quanto
          // antes; da 2ª em diante (e no fluxo normal, sempre) é o mesmo intervalo/backoff.
          const decorrido = Date.now() - inicio;
          await esperar(calcularIntervaloPolling(decorrido, op) * (falhasSeguidas + 1));
          if (!vivo()) return;
        }

        const st = await consultarStatusExportacao(idApuracao, correlationId);
        if (!vivo()) return;

        if (!st?.sucesso) {
          if (falhaTerminal(st?.httpStatus)) {
            limparCorrelationIdPersistido(idApuracao, chaveAtual);
            if (silenciosoSeNaoEncontrado) {
              atualizar({ ...ESTADO_INICIAL });
              return;
            }
            falhar(MENSAGENS_EXPORTACAO.jobNaoEncontrado);
            return;
          }
          falhasSeguidas += 1;
          if (falhasSeguidas >= op.maxFalhasSeguidas) {
            falhar(MENSAGENS_EXPORTACAO.falhaAcompanhar);
            return;
          }
        } else {
          falhasSeguidas = 0;
          if (st.status === "concluido") {
            status = st;
            break;
          }
          if (st.status === "erro" || st.status === "expirado") {
            falhar(st.mensagem || MENSAGENS_EXPORTACAO.falhaJob);
            return;
          }
          atualizar({
            estado: estadoDoStatusServidor(st.status),
            // Na fila não há progresso; processando repassa o que o servidor mandar (pode ser
            // null = indeterminado, ou 0/50 nos tipos sem loop) — sem inventar precisão.
            percentual: st.status === "processando" ? st.percentual : null,
            descricao: st.descricaoEtapa,
            correlationId,
          });
        }

        if (Date.now() - inicio >= op.tetoMs) {
          // Teto do cliente, não do job: o id persistido continua salvo de propósito —
          // um próximo mount/retomada pode encontrar o job já concluído no servidor.
          atualizar({ estado: E.TEMPO_ESGOTADO, mensagem: MENSAGENS_EXPORTACAO.tetoAtingido });
          notificar("warning", MENSAGENS_EXPORTACAO.tetoAtingido);
          return;
        }
      }

      // concluido: o worker Go já gravou o(s) arquivo(s) soltos em EXPORT_BASE_DIR (pasta
      // relatorios) — sem download pro navegador (2026-10-05). Só confirma que existe
      // pelo menos 1 arquivo antes de marcar sucesso.
      const arquivos = status.arquivos || [];
      if (arquivos.length === 0) {
        falhar(MENSAGENS_EXPORTACAO.falhaJob);
        return;
      }
      limparCorrelationIdPersistido(idApuracao, chaveAtual);
      atualizar({ estado: E.CONCLUIDO, percentual: 100, mensagem: null });
      notificar(
        "success",
        arquivos.length === 1
          ? "Arquivo gerado na pasta de relatórios."
          : `${arquivos.length} arquivos gerados na pasta de relatórios.`
      );
    },
    []
  );

  useEffect(() => {
    montadoRef.current = true;

    // Retomada: só dispara uma vez, ao montar. Se não houver id salvo pra esta
    // apuração+chave, não faz nada (comportamento idêntico ao de antes).
    const idSalvo = lerCorrelationIdPersistido(apuracaoIdRef.current, chaveRef.current);
    if (idSalvo) {
      const token = ++execucaoRef.current;
      ativoRef.current = true;
      setSnap((s) => ({ ...s, estado: E.NA_FILA, correlationId: idSalvo }));
      acompanharJob(token, idSalvo, { silenciosoSeNaoEncontrado: true });
    }

    return () => {
      // Unmount (ou troca de tela): invalida a execução corrente e corta o timer pendente.
      // O id persistido NÃO é apagado aqui — sair da tela não cancela o job no servidor.
      montadoRef.current = false;
      execucaoRef.current += 1;
      ativoRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const iniciar = useCallback(
    async (...args) => {
      if (ativoRef.current) return; // duplo clique / clique durante o job: ignora
      const token = ++execucaoRef.current;
      const vivo = () => montadoRef.current && execucaoRef.current === token;
      const idApuracao = apuracaoIdRef.current;
      const chaveAtual = chaveRef.current;

      const atualizar = (parcial) => {
        if (!vivo()) return;
        ativoRef.current = ESTADOS_ATIVOS.has(parcial.estado);
        setSnap((s) => ({ ...s, ...parcial }));
      };
      const notificar = (severity, msg) => {
        if (vivo()) onFinalizarRef.current?.({ severity, msg });
      };
      const falhar = (msg) => {
        atualizar({ estado: E.ERRO, percentual: null, descricao: null, mensagem: msg });
        notificar("error", msg);
      };

      atualizar({ ...ESTADO_INICIAL, estado: E.ENFILEIRANDO });

      let res;
      try {
        res = await enfileirarRef.current(...args);
      } catch {
        res = { sucesso: false };
      }
      if (!vivo()) return;
      if (!res?.sucesso) {
        falhar(res?.mensagem || MENSAGENS_EXPORTACAO.falhaEnfileirar);
        return;
      }
      const { correlationId } = res;
      if (!correlationId) {
        // Servidor ainda sem rastreio: não há como acompanhar — mantém o aviso de antes.
        atualizar({ ...ESTADO_INICIAL });
        notificar("success", res.mensagem || MENSAGENS_EXPORTACAO.semRastreio);
        return;
      }

      persistirCorrelationId(idApuracao, chaveAtual, correlationId);
      atualizar({ estado: estadoDoStatusServidor(res.status), correlationId });
      await acompanharJob(token, correlationId);
    },
    [acompanharJob]
  );

  return {
    iniciar,
    estado: snap.estado,
    percentual: snap.percentual,
    descricao: snap.descricao,
    mensagem: snap.mensagem,
    correlationId: snap.correlationId,
    ativo: ESTADOS_ATIVOS.has(snap.estado),
  };
}
